import { toRenderSlot, type AppState } from '../../state/appState.js';
import { createGraphNodeId } from '../../graph/nodeIds.js';
import {
  applyOutputNodeSlotMetadata,
  cloneGraphNode,
  normalizeGraphNode,
} from '../../graph/nodeDefs.js';
import type { GraphNode, NodeKind } from '../../graph/nodeDefs.js';
import { nodeDefsForKind } from '../../graph/nodeRegistry.js';

export interface EdgeRef {
  fromNode: string;
  fromPort: number;
  toNode: string;
  toPort: number;
}

export interface CreateNodeCmd {
  type: 'graph:createNode';
  node: GraphNode;
}

export interface DeleteNodeCmd {
  type: 'graph:deleteNode';
  nodeId: string;
}

export interface MoveNodeCmd {
  type: 'graph:moveNode';
  nodeId: string;
  x: number;
  y: number;
}

export interface SetNodeDataCmd {
  type: 'graph:setData';
  nodeId: string;
  field: string;
  value: unknown;
}

export interface ReplaceNodeCmd {
  type: 'graph:replaceNode';
  nodeId: string;
  node: GraphNode;
  incomingEdges: EdgeRef[];
}

export interface ConnectCmd {
  type: 'graph:connect';
  edge: EdgeRef;
  isReconnect?: boolean;
}

export interface ReconnectEdgeCmd {
  type: 'graph:reconnect';
  fromEdge: EdgeRef;
  toEdge: EdgeRef;
}

export interface CutEdgesCmd {
  type: 'graph:cutEdges';
  edges: EdgeRef[];
}

export interface SetParamValueCmd {
  type: 'params:setValue';
  key: string;
  value: unknown;
}

export function createNodeCommand(appState: AppState, nodeType: string, x: number, y: number): CreateNodeCmd | null {
  const defs = nodeDefsForKind(appState.editorGraphKind);
  const def = defs[nodeType];
  if (!def) return null;

  const outputType = 'Output';
  if (
    nodeType === outputType
    && appState.editorGraphKind !== 'data'
    && appState.nodes.some(n => n.type === outputType)
  ) return null;

  const node: GraphNode = normalizeGraphNode({
    id: createGraphNodeId(),
    type: nodeType as NodeKind,
    x,
    y,
    data: def.defaults,
  }, defs);

  if (nodeType === 'Output' && appState.editorGraphKind !== 'data') {
    applyOutputNodeSlotMetadata(node, toRenderSlot(appState.editorGraphSlot));
  } else if (nodeType === 'Output') {
    const names = new Set(
      appState.nodes
        .filter(candidate => candidate.type === 'Output')
        .map(candidate => String(candidate.data.inputName || 'Output')),
    );
    let index = 1;
    let name = 'Output';
    while (names.has(name)) name = `Output ${++index}`;
    node.data.inputName = name;
    node.data.inputType = 'float';
    node.data.displayType = 'float';
  }

  return {
    type: 'graph:createNode',
    node,
  };
}

export function createMoveNodeCommand(nodeId: string, x: number, y: number): MoveNodeCmd {
  return { type: 'graph:moveNode', nodeId, x, y };
}

export function createDeleteNodeCommand(nodeId: string): DeleteNodeCmd {
  return { type: 'graph:deleteNode', nodeId };
}

export function createSetNodeDataCommand(nodeId: string, field: string, value: unknown): SetNodeDataCmd {
  return { type: 'graph:setData', nodeId, field, value };
}

export function createReplaceNodeCommand(nodeId: string, node: GraphNode, incomingEdges: EdgeRef[] = []): ReplaceNodeCmd {
  return { type: 'graph:replaceNode', nodeId, node: cloneGraphNode(node), incomingEdges: incomingEdges.map(edge => ({ ...edge })) };
}

export function createConnectCommand(edge: EdgeRef, isReconnect = false): ConnectCmd {
  return { type: 'graph:connect', edge: { ...edge }, isReconnect };
}

export function createReconnectEdgeCommand(fromEdge: EdgeRef, toEdge: EdgeRef): ReconnectEdgeCmd {
  return { type: 'graph:reconnect', fromEdge: { ...fromEdge }, toEdge: { ...toEdge } };
}

export function createSetParamValueCommand(key: string, value: unknown): SetParamValueCmd {
  return { type: 'params:setValue', key, value };
}

export function createCutEdgesCommand(edges: EdgeRef[]): CutEdgesCmd {
  return { type: 'graph:cutEdges', edges: edges.map(edge => ({ ...edge })) };
}
